import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {Folder, FolderUID} from "../../../file-manager/types/folder";
import {File, FileUID} from "../../../file-manager/types/file";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Uppy} from "@uppy/core";
import {FileManagerService} from "../../../file-manager/file-manager.service";
import {UppyFactory} from "../../../file-manager/uppy.factory";
import {environment} from "../../../../environments/environment";
import {RenameFileComponent} from "../rename-file/rename-file.component";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {ConfirmDialogComponent} from "../../../components/confirm-dialog/confirm-dialog.component";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'telemann-file-manager',
    templateUrl: './file-manager.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: [
        './file-manager.component.scss',
        '../../../../../node_modules/@uppy/core/src/style.scss',
        '../../../../../node_modules/@uppy/dashboard/src/style.scss',
    ],
    standalone: false
})
export class FileManagerComponent implements OnInit {
  public folders: Folder[];
  public files: File[];
  public selectedFolder: null | Folder = null;
  public selectedFiles: File[] = [];
  public editedFolderId: null | FolderUID = null;
  public movedFileId: null | FileUID = null;

  public createFolderForm: FormGroup;
  public renameFolderForm: FormGroup;
  public isLoading: boolean = false;

  public uppy: Uppy;

  public constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private fileManagerService: FileManagerService,
    private translateService: TranslateService,
  ) {
    this.createFolderForm = new FormGroup({
      name: new FormControl('', Validators.required),
    })
  }

  ngOnInit(): void {
    this.fileManagerService.listFolders().subscribe(
      folders => {
        this.folders = folders;
      }
    );

    this.uppy = UppyFactory.create();
    this.uppy.on('complete', (result) => {
      result.successful.forEach(file => {
        this.files.push({
          id: parseInt(file.response.body.id.toString()),
          url: '',
          name: file.response.body.name.toString(),
          label: file.response.body.label?.toString() ?? '',
          labels: (file.response.body.labels ?? {}) as Record<string, string | null>,
          thumbnail: file.response.body.thumbnail?.toString(),
        })
      });

      if (result.failed.length === 0) {
      }
    })
  }

  public createFolder(): void {
    let folderData = this.createFolderForm.getRawValue();
    let maxOrder = Math.max(...this.folders.map(f => f.sortOrder));
    this.fileManagerService.createFolder(
      folderData.name,
      maxOrder + 1
    ).subscribe(
      (newFolder: Folder) => {
        this.folders.push(newFolder);
        this.createFolderForm.reset();
      }
    )
  }

  public openFolderRenameForm(folder: Folder): void {
    this.renameFolderForm = this.createRenameFolderForm(folder);
    this.editedFolderId = folder.id;
  }

  public renameFolder(): void {
    let folderData = this.renameFolderForm.getRawValue();
    this.fileManagerService.renameFolder(
      folderData.id,
      folderData.name,
    ).subscribe(
      result => {
        const index = this.folders.findIndex((f) => f.id === result.id);
        this.folders[index] = result;
        this.editedFolderId = null;
      }
    )
  }

  public deleteFolder(folder: Folder): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('fileManager.deleteFolder'),
        message: this.translateService.instant('fileManager.confirmDelete', { name: folder.name }),
        confirmText: this.translateService.instant('common.delete'),
        cancelText: this.translateService.instant('common.cancel'),
        confirmColor: 'warn'
      },
      width: '400px'
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.fileManagerService.deleteFolder(folder.id).subscribe({
        next: () => {
          this.folders = this.folders.filter(f => f !== folder);
          this.snackBar.open(
            this.translateService.instant('fileManager.folderDeleted'),
            this.translateService.instant('common.close'),
            { duration: 3000 }
          );
        },
        error: () => {
          this.snackBar.open(
            this.translateService.instant('fileManager.deleteFailed'),
            this.translateService.instant('common.close'),
            { duration: 5000 }
          );
        }
      });
    });
  }

  public deleteFile(file: File): void {
    const displayName = file.label || file.name;
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('fileManager.deleteFile'),
        message: this.translateService.instant('fileManager.confirmDelete', { name: displayName }),
        confirmText: this.translateService.instant('common.delete'),
        cancelText: this.translateService.instant('common.cancel'),
        confirmColor: 'warn'
      },
      width: '400px'
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.fileManagerService.deleteFile(file.id).subscribe({
        next: () => {
          this.files = this.files.filter(f => f !== file);
          this.snackBar.open(
            this.translateService.instant('fileManager.fileDeleted'),
            this.translateService.instant('common.close'),
            { duration: 3000 }
          );
        },
        error: () => {
          this.snackBar.open(
            this.translateService.instant('fileManager.deleteFailed'),
            this.translateService.instant('common.close'),
            { duration: 5000 }
          );
        }
      });
    });
  }

  public openFileMoveForm(file: File): void {
    this.movedFileId = file.id;
  }

  public getFileUrl(file: File): string {
    return `${environment.cdn.url}/cdn/${file.name}`
  }

  public getFileDownloadLink(file: File): string {
    return `${environment.api.url}/ui/v1/file/download/${file.id}`;
  }

  public moveFile(folder: Folder): void {
    this.fileManagerService.moveFile(
      this.movedFileId,
      folder.id,
    ).subscribe((file: File) => {
        this.files = this.files.filter(f => f.id !== file.id);
        this.movedFileId = null;
      }
    )
  }

  public rename(file: File) {
    this.dialog.open(RenameFileComponent, {
      data: {
        file: file
      }
    }).afterClosed().subscribe(updatedFile => {
      if (updatedFile) {
        const existingFile = this.files.find(f => f.id === updatedFile.id);
        if (existingFile) {
          existingFile.label = updatedFile.label;
          existingFile.labels = updatedFile.labels;
        }
      }
    });
  }

  public selectFolder(folder: Folder): void {
    this.isLoading = true;
    this.fileManagerService.getFiles(folder.id).subscribe(
      (files: File[]) => {
        this.isLoading = false;
        this.files = files;
        this.selectedFolder = folder;
        this.selectedFiles = [];
        this.movedFileId = null;
        this.editedFolderId = null;

        this.uppy.setMeta({
          folderId: folder.id
        })
      }
    )
  }

  public getFilePreviewStyles(file: File): object {
    let styles: any = {};

    if (file.thumbnail) {
      styles.backgroundImage = `url(${this.getFileThumbnailUrl(file)})`;
    }

    return styles;
  }

  public getFileThumbnailUrl(file: File): string | null {
    if (file.thumbnail) {
      return `${environment.cdn.url}/cdn/thumbnails/${file.thumbnail}`
    }

    return null;
  }

  public getInputType(): string {
    return 'checkbox';
  }

  public selectFile($event, file: File): void {
    let selected = $event.target.checked;
    if (selected) {
      this.selectedFiles.push(file);
    } else {
      this.selectedFiles = this.selectedFiles.filter(f => f !== file);
    }
  }

  private createRenameFolderForm(folder: Folder): FormGroup {
    return new FormGroup({
      id: new FormControl(folder.id, Validators.required),
      name: new FormControl(folder.name, Validators.required),
    })
  }

  public closeFolderEditForm(): void {
    this.editedFolderId = null;
  }

  public sortFolders($event: CdkDragDrop<Folder[]>): void {
    // needs to be done this way to refresh the view
    let items = structuredClone(this.folders);
    moveItemInArray(items, $event.previousIndex, $event.currentIndex);
    this.folders = items;

    this.reorderFolders();
    this.updateFolders(this.folders);
  }

  private reorderFolders(): void {
    let order = 1;
    this.folders.forEach(f => {
      f.sortOrder = order++;
    })
  }

  private updateFolders(folders: Folder[]): void {
    this.fileManagerService.updateFolders(folders).subscribe(
      folders => {
        this.folders = folders;
      }
    );
  }
}
